import { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { Plus, UserPlus } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Label } from "~/components/ui/label";
import { SearchableSelect } from "~/components/ui/searchable-select";
import { MAQUILA_CLIENTS_KEY, useMaquilaClientList } from "~/hooks/maquila";
import { formatRut } from "~/utils/helpers";

import { AddMaquilaClientDialog } from "./AddMaquilaClientDialog";
import { ImportClientDialog } from "./ImportClientDialog";

interface MaquilaClientSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  label?: string;
}

/**
 * "Cliente maquila" selector with an inline "Agregar" action to create a new
 * maquila client or import an existing one — so recipe/consumption authors can
 * add a client without leaving the flow. Shows the RUT under each name.
 */
export function MaquilaClientSelect({
  value,
  onValueChange,
  label = "Cliente maquila *",
}: MaquilaClientSelectProps) {
  const queryClient = useQueryClient();
  const { data: clients = [] } = useMaquilaClientList();
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const handleClientReady = (client?: { id: string; name: string }) => {
    queryClient.invalidateQueries({ queryKey: MAQUILA_CLIENTS_KEY });
    if (client?.id) onValueChange(client.id);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-my-1 h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" />
              Agregar
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setImportOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              Importar cliente existente
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Crear cliente nuevo
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <SearchableSelect
        value={value}
        onValueChange={onValueChange}
        options={clients.map((c) => ({
          value: c.id,
          label: c.name,
          description: c.tax_id ? formatRut(c.tax_id) : undefined,
          searchTerms: `${c.name} ${c.tax_id ?? ""}`,
        }))}
        placeholder="Seleccionar cliente"
        emptyMessage="No hay clientes de maquila. Agrega o importa uno."
      />

      <AddMaquilaClientDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onClientAdded={handleClientReady}
      />
      <ImportClientDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onClientImported={handleClientReady}
      />
    </div>
  );
}
